import {
  TStepGroup,
  TWorkflowDefinition,
  TWorkflowStepDefinition,
  TWorkflowStepTransition,
  TWorkflowTriggerDefinition,
} from '@commenda/types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TSelectedEntity } from '../types/node';

interface IWorkflowContextType {
  // State
  workflow: TWorkflowDefinition | null;
  selectedEntities: TSelectedEntity[];

  // Unsaved changes detection
  hasUnsavedChanges: () => boolean;
  markAsSaved: () => void;

  // History for undo/redo
  canUndo: boolean;
  canRedo: boolean;

  // Actions
  setWorkflow: (workflow: TWorkflowDefinition) => void;
  /** Distinct from setWorkflow, which leaves the saved baseline untouched and so would mark the canvas
   * permanently dirty: the next pre-send save would write this stale graph over the agent's work. */
  applyRemoteWorkflow: (workflow: TWorkflowDefinition) => void;
  /** True while the agent is writing to this version, or while the version in view is not editable. */
  isReadOnly: boolean;
  setIsReadOnly: (isReadOnly: boolean) => void;
  addStep: (step: TWorkflowStepDefinition) => void;
  updateStep: (
    stepId: string,
    updates: Partial<TWorkflowStepDefinition>
  ) => void;
  removeStep: (stepId: string) => void;
  /** Applies a change that spans several steps or transitions as one undoable action. */
  updateWorkflow: (
    updater: (workflow: TWorkflowDefinition) => TWorkflowDefinition
  ) => void;
  addTransition: (transition: TWorkflowStepTransition) => void;
  updateTransition: (
    fromId: string,
    toId: string,
    updates: Partial<TWorkflowStepTransition>
  ) => void;
  removeTransition: (id: string) => void;
  setSelectedEntities: (entities: TSelectedEntity[]) => void;
  undo: () => void;
  redo: () => void;
  saveCheckpoint: () => void;
  addStepGroup: (stepGroup: TStepGroup) => void;
  updateStepGroup: (stepGroupId: string, updates: Partial<TStepGroup>) => void;
  removeStepGroup: (stepGroupId: string) => void;
  reorderStepGroups: (newOrder: TStepGroup[]) => void;
  updateStepGroups: (
    updates: { groupId: string; updates: Partial<TStepGroup> }[]
  ) => void;
  addTrigger: (trigger: TWorkflowTriggerDefinition) => void;
  updateTrigger: (
    triggerId: string,
    updates: Partial<TWorkflowTriggerDefinition>
  ) => void;
  removeTrigger: (triggerId: string) => void;
}

const WorkflowContext = createContext<IWorkflowContextType | undefined>(
  undefined
);

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

interface WorkflowProviderProps {
  children: React.ReactNode;
  initialWorkflow?: TWorkflowDefinition | null;
}

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({
  children,
  initialWorkflow = null,
}) => {
  const [workflow, setWorkflowState] = useState<TWorkflowDefinition | null>(
    initialWorkflow
  );
  const [selectedEntities, setSelectedEntities] = useState<TSelectedEntity[]>(
    []
  );
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Unsaved changes detection via comparison
  const savedWorkflowJsonRef = useRef(JSON.stringify(initialWorkflow));
  const workflowRef = useRef(workflow);
  workflowRef.current = workflow;

  const hasUnsavedChanges = useCallback(
    () => JSON.stringify(workflowRef.current) !== savedWorkflowJsonRef.current,
    []
  );

  const markAsSaved = useCallback(() => {
    savedWorkflowJsonRef.current = JSON.stringify(workflowRef.current);
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // History management
  const historyRef = useRef<TWorkflowDefinition[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const [historyVersion, setHistoryVersion] = useState(0); // To trigger re-renders

  const saveCheckpoint = useCallback(() => {
    if (!workflow) return;

    // Remove any history after current index
    historyRef.current = historyRef.current.slice(
      0,
      historyIndexRef.current + 1
    );

    // Add current state to history
    historyRef.current.push(JSON.parse(JSON.stringify(workflow)));
    historyIndexRef.current += 1;

    // Limit history to last 50 states
    if (historyRef.current.length > 50) {
      historyRef.current = historyRef.current.slice(-50);
      historyIndexRef.current = historyRef.current.length - 1;
    }

    setHistoryVersion((v) => v + 1);
  }, [workflow]);

  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      const previousState = historyRef.current[historyIndexRef.current];
      setWorkflowState(JSON.parse(JSON.stringify(previousState)));
      setHistoryVersion((v) => v + 1);
    }
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      const nextState = historyRef.current[historyIndexRef.current];
      setWorkflowState(JSON.parse(JSON.stringify(nextState)));
      setHistoryVersion((v) => v + 1);
    }
  }, []);

  const setWorkflow = useCallback((newWorkflow: TWorkflowDefinition) => {
    setWorkflowState(newWorkflow);
    historyRef.current = [JSON.parse(JSON.stringify(newWorkflow))];
    historyIndexRef.current = 0;
    setHistoryVersion((v) => v + 1);
  }, []);

  const applyRemoteWorkflow = useCallback(
    (remoteWorkflow: TWorkflowDefinition) => {
      const serialized = JSON.stringify(remoteWorkflow);

      // markAsSaved reads workflowRef, which is only refreshed on the next render, so it would snapshot
      // the previous graph and leave the canvas dirty.
      savedWorkflowJsonRef.current = serialized;
      workflowRef.current = remoteWorkflow;
      setWorkflowState(remoteWorkflow);

      // Append rather than reset, so the author can still undo past the agent's change.
      historyRef.current = [
        ...historyRef.current.slice(0, historyIndexRef.current + 1),
        JSON.parse(serialized) as TWorkflowDefinition,
      ].slice(-50);
      historyIndexRef.current = historyRef.current.length - 1;
      setHistoryVersion((v) => v + 1);
    },
    []
  );

  const addStep = useCallback(
    (step: TWorkflowStepDefinition) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          steps: [...prev.steps, step],
        };
      });
      console.log('Saved');
    },
    [saveCheckpoint]
  );

  const updateStep = useCallback(
    (stepId: string, updates: Partial<TWorkflowStepDefinition>) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          steps: prev.steps.map((step) =>
            step.id === stepId ? { ...step, ...updates } : step
          ),
        };
      });
    },
    [saveCheckpoint]
  );

  const removeStep = useCallback(
    (stepId: string) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          steps: prev.steps.filter((s) => s.id !== stepId),
          stepTransitions: prev.stepTransitions.filter(
            (t) => t.fromStepId !== stepId && t.toStepId !== stepId
          ),
          stepGroups: prev.stepGroups
            ? prev.stepGroups.map((group) => ({
                ...group,
                stepIds: group.stepIds.filter((id) => id !== stepId),
              }))
            : prev.stepGroups,
        };
      });
    },
    [saveCheckpoint]
  );

  const updateWorkflow = useCallback(
    (updater: (workflow: TWorkflowDefinition) => TWorkflowDefinition) => {
      saveCheckpoint();
      setWorkflowState((prev) => (prev ? updater(prev) : null));
    },
    [saveCheckpoint]
  );

  const addTransition = useCallback(
    (transition: TWorkflowStepTransition) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          stepTransitions: [...prev.stepTransitions, transition],
        };
      });
    },
    [saveCheckpoint]
  );

  const updateTransition = useCallback(
    (
      fromId: string,
      toId: string,
      updates: Partial<TWorkflowStepTransition>
    ) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          stepTransitions: prev.stepTransitions.map((t) =>
            t.fromStepId === fromId && t.toStepId === toId
              ? { ...t, ...updates }
              : t
          ),
        };
      });
    },
    [saveCheckpoint]
  );

  const removeTransition = useCallback(
    (id: string) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          stepTransitions: prev.stepTransitions.filter((t) => t.id !== id),
        };
      });
    },
    [saveCheckpoint]
  );

  const addStepGroup = useCallback(
    (stepGroup: TStepGroup) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        const prevStepGroups = prev.stepGroups || [];
        return {
          ...prev,
          stepGroups: [...prevStepGroups, stepGroup],
        };
      });
    },
    [saveCheckpoint]
  );

  const updateStepGroup = useCallback(
    (stepGroupId: string, updates: Partial<TStepGroup>) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          stepGroups: prev.stepGroups.map((stepGroup) =>
            stepGroup.id === stepGroupId
              ? ({ ...stepGroup, ...updates } as TStepGroup)
              : stepGroup
          ),
        };
      });
    },
    [saveCheckpoint]
  );

  const removeStepGroup = useCallback(
    (stepGroupId: string) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          stepGroups: prev.stepGroups.filter(
            (stepGroup) => stepGroup.id !== stepGroupId
          ),
        };
      });
    },
    [saveCheckpoint]
  );

  const reorderStepGroups = useCallback(
    (newOrder: TStepGroup[]) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          stepGroups: newOrder,
        };
      });
    },
    [saveCheckpoint]
  );

  const updateStepGroups = useCallback(
    (updates: { groupId: string; updates: Partial<TStepGroup> }[]) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          stepGroups: prev.stepGroups.map((stepGroup) => {
            const update = updates.find((u) => u.groupId === stepGroup.id);
            return update ? { ...stepGroup, ...update.updates } : stepGroup;
          }),
        };
      });
    },
    [saveCheckpoint]
  );

  const addTrigger = useCallback(
    (trigger: TWorkflowTriggerDefinition) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          triggers: [...(prev.triggers ?? []), trigger],
        };
      });
    },
    [saveCheckpoint]
  );

  const updateTrigger = useCallback(
    (triggerId: string, updates: Partial<TWorkflowTriggerDefinition>) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          triggers: (prev.triggers ?? []).map((t) =>
            t.id === triggerId
              ? ({ ...t, ...updates } as TWorkflowTriggerDefinition)
              : t
          ),
        };
      });
    },
    [saveCheckpoint]
  );

  const removeTrigger = useCallback(
    (triggerId: string) => {
      saveCheckpoint();
      setWorkflowState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          triggers: (prev.triggers ?? []).filter((t) => t.id !== triggerId),
        };
      });
    },
    [saveCheckpoint]
  );

  const value: IWorkflowContextType = useMemo(
    () => ({
      workflow,
      hasUnsavedChanges,
      markAsSaved,
      canUndo: historyIndexRef.current > 0,
      canRedo: historyIndexRef.current < historyRef.current.length - 1,
      setWorkflow,
      applyRemoteWorkflow,
      isReadOnly,
      setIsReadOnly,
      addStep,
      updateStep,
      removeStep,
      updateWorkflow,
      addTransition,
      updateTransition,
      removeTransition,
      selectedEntities,
      setSelectedEntities,
      undo,
      redo,
      saveCheckpoint,
      addStepGroup,
      updateStepGroup,
      removeStepGroup,
      reorderStepGroups,
      updateStepGroups,
      addTrigger,
      updateTrigger,
      removeTrigger,
    }),
    [
      workflow,
      hasUnsavedChanges,
      markAsSaved,
      setWorkflow,
      applyRemoteWorkflow,
      isReadOnly,
      addStep,
      updateStep,
      removeStep,
      updateWorkflow,
      addTransition,
      updateTransition,
      removeTransition,
      selectedEntities,
      undo,
      redo,
      saveCheckpoint,
      addStepGroup,
      updateStepGroup,
      removeStepGroup,
      reorderStepGroups,
      updateStepGroups,
      addTrigger,
      updateTrigger,
      removeTrigger,
    ]
  );

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};
